import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, FileText, Image as ImageIcon, Mail, Presentation, CreditCard, File, CheckCircle, ExternalLink, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { AIResources } from '../components/sections/AIResources';
import { BrandBadge } from '../components/sections/BrandBadge';

// Each template now ships in multiple native formats. The download buttons
// fan out below the card so the user can pick the format they need.
const templates = {
  documents: [
    {
      id: 'template-1-professional-report',
      name: 'Template 1: Professional Report',
      description: 'Comprehensive report template for quarterly reports, project summaries, and formal documentation.',
      icon: FileText,
      preview: 'Executive summary, findings & analysis, recommendations. Clean professional layout.',
      files: [
        { format: 'DOCX', file: '/templates/template-1-professional-report.docx', size: '76 KB' },
        { format: 'HTML', file: '/templates/template-1-professional-report.html', size: '5 KB' },
      ],
    },
    {
      id: 'template-2-technical-spec',
      name: 'Template 2: Technical Specification',
      description: 'Detailed technical documentation with specifications tables and performance parameters.',
      icon: File,
      preview: 'Blue header bar with logo, branded specification tables, material requirements, technical details.',
      files: [
        { format: 'DOCX', file: '/templates/template-2-technical-spec.docx', size: '76 KB' },
        { format: 'HTML', file: '/templates/template-2-technical-spec.html', size: '6 KB' },
      ],
    },
    {
      id: 'template-3-business-proposal',
      name: 'Template 3: Business Proposal',
      description: 'Client-facing proposal template with pricing tables and executive summary.',
      icon: File,
      preview: 'Cover page with logo, client info section, investment summary, pricing table, CTA section.',
      files: [
        { format: 'DOCX', file: '/templates/template-3-business-proposal.docx', size: '76 KB' },
        { format: 'HTML', file: '/templates/template-3-business-proposal.html', size: '8 KB' },
      ],
    },
    {
      id: 'template-4-meeting-minutes',
      name: 'Template 4: Meeting Minutes',
      description: 'Structured meeting notes with action items, attendees, and decisions.',
      icon: FileText,
      preview: 'Meeting info grid, attendees list, discussion items, action items table with priorities.',
      files: [
        { format: 'DOCX', file: '/templates/template-4-meeting-minutes.docx', size: '76 KB' },
        { format: 'HTML', file: '/templates/template-4-meeting-minutes.html', size: '8 KB' },
      ],
    },
    {
      id: 'template-5-one-page-summary',
      name: 'Template 5: One-Page Summary',
      description: 'Concise executive summary with key metrics and visual highlights.',
      icon: FileText,
      preview: 'Compact layout, key highlights box, metrics grid, two-column info sections, action bar.',
      files: [
        { format: 'DOCX', file: '/templates/template-5-one-page-summary.docx', size: '76 KB' },
        { format: 'HTML', file: '/templates/template-5-one-page-summary.html', size: '7 KB' },
      ],
    },
    {
      id: 'template-6-project-status',
      name: 'Template 6: Project Status Update',
      description: 'Weekly project status updates with traffic light indicators, timeline, and action items.',
      icon: FileText,
      preview: 'Status cards (On Track/At Risk/Blocked), project timeline, task updates, action items table with priorities.',
      files: [
        { format: 'DOCX', file: '/templates/template-6-project-status.docx', size: '77 KB' },
        { format: 'PPTX', file: '/templates/template-6-project-status.pptx', size: '34 KB' },
        { format: 'HTML', file: '/templates/template-6-project-status.html', size: '13 KB' },
      ],
    },
    {
      id: 'template-7-internal-memo',
      name: 'Template 7: Internal Memo',
      description: 'Professional internal memorandum for company communications and announcements.',
      icon: Mail,
      preview: 'Formal memo layout with TO/FROM/DATE/RE fields, highlight boxes, action items with checkboxes.',
      files: [
        { format: 'DOCX', file: '/templates/template-7-internal-memo.docx', size: '77 KB' },
        { format: 'PPTX', file: '/templates/template-7-internal-memo.pptx', size: '29 KB' },
        { format: 'HTML', file: '/templates/template-7-internal-memo.html', size: '8 KB' },
      ],
    },
  ],
  email: [
    {
      id: 'email-signature',
      name: 'Email Signature',
      description: 'Professional email signature with contact details.',
      icon: Mail,
      preview: 'Logo, name, title, contact information, and tagline. Paste-ready HTML.',
      files: [{ format: 'HTML', file: '/templates/email-signature.html', size: '2 KB' }],
    },
    {
      id: 'letterhead',
      name: 'Letterhead',
      description: 'Branded letterhead for formal correspondence.',
      icon: FileText,
      preview: 'Jackstone blue header, white space for content, footer with contact details.',
      files: [{ format: 'HTML', file: '/templates/letterhead.html', size: '3 KB' }],
    },
  ],
  presentations: [
    {
      id: 'presentation-template',
      name: 'Presentation Deck',
      description: 'Branded 5-slide PowerPoint deck — title, section divider, content, metrics, closing.',
      icon: Presentation,
      preview: 'Master slides with Jackstone branding, Bebas Neue headings, Montserrat body, brand colors.',
      files: [{ format: 'PPTX', file: '/templates/presentation-template.pptx', size: '107 KB' }],
    },
  ],
  businessCards: [
    {
      id: 'business-card',
      name: 'Business Card (Front + Back)',
      description: 'Print-ready 3.5" x 2" business card at 300 DPI.',
      icon: CreditCard,
      preview: 'Front: blue panel with logo, name, title, contact info. Back: full blue with centered logo and tagline.',
      files: [
        { format: 'PDF', file: '/templates/business-card.pdf', size: '50 KB' },
        { format: 'PNG (Front)', file: '/templates/business-card-front.png', size: '32 KB' },
        { format: 'PNG (Back)', file: '/templates/business-card-back.png', size: '21 KB' },
      ],
    },
  ],
  social: [
    {
      id: 'linkedin-post',
      name: 'LinkedIn Post Template',
      description: '1200x627px branded social graphic for LinkedIn posts and link previews.',
      icon: ImageIcon,
      preview: 'Split layout, blue logo panel, headline in Bebas Neue, tagline in Montserrat.',
      files: [
        { format: 'PNG', file: '/templates/linkedin-post-template.png', size: '26 KB' },
        { format: 'PDF', file: '/templates/linkedin-post-template.pdf', size: '32 KB' },
      ],
    },
    {
      id: 'instagram-post',
      name: 'Instagram Post Template',
      description: '1080x1080px square branded social graphic for Instagram.',
      icon: ImageIcon,
      preview: 'Blue hero half with white logo, white half with centered headline and tagline.',
      files: [
        { format: 'PNG', file: '/templates/instagram-post-template.png', size: '34 KB' },
        { format: 'PDF', file: '/templates/instagram-post-template.pdf', size: '42 KB' },
      ],
    },
  ],
};

const formatExtension = (format) => {
  const f = format.toLowerCase();
  if (f.includes('png')) return 'png';
  if (f.includes('pdf')) return 'pdf';
  if (f.includes('docx')) return 'docx';
  if (f.includes('pptx')) return 'pptx';
  if (f.includes('html')) return 'html';
  return f;
};

export default function TemplateDownloads() {
  const [downloadedFiles, setDownloadedFiles] = useState(new Set());

  const handleDownload = (template, fileEntry) => {
    const link = document.createElement('a');
    link.href = fileEntry.file;
    const ext = formatExtension(fileEntry.format);
    const suffix = fileEntry.format.includes('(') ? `-${fileEntry.format.match(/\(([^)]+)\)/)[1].toLowerCase()}` : '';
    link.download = `jackstone-${template.id}${suffix}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    const key = `${template.id}-${fileEntry.format}`;
    setDownloadedFiles(new Set([...downloadedFiles, key]));
    toast.success(`${template.name} (${fileEntry.format}) downloaded`);
  };

  const handlePreview = (template, fileEntry) => {
    // Open HTML templates in a new tab for preview
    window.open(fileEntry.file, '_blank');
    toast.success(`Opening ${template.name} preview`);
  };

  const TemplateCard = ({ template }) => {
    const Icon = template.icon;

    return (
      <Card className="p-6 hover:shadow-lg transition-smooth" data-testid={`template-card-${template.id}`}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-jackstone-blue/10">
            <Icon className="h-6 w-6 text-jackstone-blue" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-body text-lg mb-1">
              {template.name}
            </h3>
            <p className="text-sm text-subtle mb-2">
              {template.description}
            </p>
            <p className="text-xs text-subtle mb-4">
              {template.preview}
            </p>

            <div className="flex flex-wrap gap-2">
              {template.files.map((fileEntry) => {
                const key = `${template.id}-${fileEntry.format}`;
                const isDownloaded = downloadedFiles.has(key);
                const isHtml = fileEntry.format === 'HTML';
                
                return (
                  <Button
                    key={key}
                    onClick={() => isHtml ? handlePreview(template, fileEntry) : handleDownload(template, fileEntry)}
                    size="sm"
                    variant={isHtml ? "outline" : "default"}
                    className="gap-2"
                    data-testid={`download-${template.id}-${formatExtension(fileEntry.format)}`}
                  >
                    {isHtml ? (
                      <ExternalLink className="h-4 w-4" />
                    ) : isDownloaded ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    {isHtml ? 'Preview' : fileEntry.format}
                    <span className="text-xs opacity-70">{fileEntry.size}</span>
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </Card>
    );
  };

  const allFiles = Object.values(templates).flat();
  const totalDownloads = allFiles.reduce((acc, t) => acc + t.files.length, 0);
  const totalTemplates = allFiles.length;

  return (
    <div className="min-h-screen bg-background">
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border"
      >
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-blue.png" alt="Jackstone" className="h-8" data-testid="header-logo" />
            <div className="hidden sm:block h-6 w-px bg-border"></div>
            <div className="hidden sm:block text-sm text-subtle font-medium">
              Marketing Template Downloads
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.location.href = '/compliance'}
              className="hidden md:flex gap-2"
              data-testid="header-compliance-link"
            >
              <ShieldCheck className="h-4 w-4" />
              Compliance
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = '/'}
              data-testid="back-to-guidelines-btn"
            >
              Back to Brand Guidelines
            </Button>
          </div>
        </div>
      </motion.header>

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-display font-normal text-jackstone-blue mb-4 uppercase">
              Marketing Template Suite
            </h1>
            <p className="text-lg text-body leading-relaxed max-w-3xl mx-auto">
              Download professionally designed templates that follow the Jackstone brand guidelines.
              Each template ships in multiple native formats — DOCX, PPTX, PDF, PNG, and HTML.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <Card className="p-6 text-center" data-testid="stat-templates">
              <div className="text-3xl font-display font-normal text-jackstone-blue mb-1">
                {totalTemplates}
              </div>
              <div className="text-sm text-subtle">Templates</div>
            </Card>
            <Card className="p-6 text-center" data-testid="stat-downloads-available">
              <div className="text-3xl font-display font-normal text-jackstone-blue mb-1">
                {totalDownloads}
              </div>
              <div className="text-sm text-subtle">Files Available</div>
            </Card>
            <Card className="p-6 text-center" data-testid="stat-downloaded">
              <div className="text-3xl font-display font-normal text-jackstone-blue mb-1">
                {downloadedFiles.size}
              </div>
              <div className="text-sm text-subtle">Downloaded</div>
            </Card>
            <Card className="p-6 text-center">
              <div className="text-3xl font-display font-normal text-jackstone-blue mb-1">
                100%
              </div>
              <div className="text-sm text-subtle">Brand Compliant</div>
            </Card>
          </div>

          <Tabs defaultValue="documents" className="space-y-8">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-5">
              <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
              <TabsTrigger value="email" data-testid="tab-email">Email</TabsTrigger>
              <TabsTrigger value="presentations" data-testid="tab-presentations">Presentations</TabsTrigger>
              <TabsTrigger value="business-cards" data-testid="tab-business-cards">Business Cards</TabsTrigger>
              <TabsTrigger value="social" data-testid="tab-social">Social Media</TabsTrigger>
            </TabsList>

            <TabsContent value="documents" className="space-y-4" data-testid="content-documents">
              <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
                Document Templates
              </h2>
              {templates.documents.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </TabsContent>

            <TabsContent value="email" className="space-y-4" data-testid="content-email">
              <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
                Email & Letterhead Templates
              </h2>
              {templates.email.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </TabsContent>

            <TabsContent value="presentations" className="space-y-4" data-testid="content-presentations">
              <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
                Presentation Templates
              </h2>
              {templates.presentations.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </TabsContent>

            <TabsContent value="business-cards" className="space-y-4" data-testid="content-business-cards">
              <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
                Business Card Templates
              </h2>
              {templates.businessCards.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </TabsContent>

            <TabsContent value="social" className="space-y-4" data-testid="content-social">
              <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
                Social Media Templates
              </h2>
              {templates.social.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </TabsContent>
          </Tabs>

          <Card className="p-8 bg-light-blue border-jackstone-blue/20 mt-12">
            <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
              How to Use These Templates
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-body mb-2">DOCX & PPTX Files</h4>
                <ol className="text-sm text-body space-y-2 list-decimal list-inside">
                  <li>Download and open in Microsoft Word or PowerPoint</li>
                  <li>Headings use Bebas Neue — install the font for the cleanest result</li>
                  <li>If Bebas Neue is unavailable, Word will substitute with Impact</li>
                  <li>Body copy uses Montserrat; install via Google Fonts if needed</li>
                  <li>Replace placeholder content with your own copy</li>
                </ol>
              </div>
              <div>
                <h4 className="font-medium text-body mb-2">PDF / PNG / HTML Files</h4>
                <ul className="text-sm text-body space-y-2 list-disc list-inside">
                  <li>PDFs are print-ready at 300 DPI (business cards, social posts)</li>
                  <li>PNGs are pixel-perfect for web and social media</li>
                  <li>HTML files open in any browser — print to PDF as needed</li>
                  <li>Keep colors, fonts, and logo placement consistent</li>
                  <li>Refer to the brand guidelines for full usage rules</li>
                </ul>
              </div>
            </div>
          </Card>

          <AIResources />

          <BrandBadge />
        </div>
      </main>
    </div>
  );
}
